import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Container, Row } from 'reactstrap';
import {
    Alignment,
    DateInput,
    Icon,
    InputType,
    Item,
    MultiSelectList,
    NumericInput,
    SelectInput,
    TextArea,
    ToogleSwitch,
} from 'tacho-ui';
import config from '../../../config';
import { defaultGenerateLicenseModel, GenerateLicenseModel } from '../../../core/domainModels/licences/GenerateLicense';
import { generateGuid } from '../../../core/helpers/guid';
import { ApplicationState } from '../../../state/ApplicationState';
import { DispatchType } from '../../../state/configureStore';
import { fetchLicenceThunk, generateLicenceThunk } from '../../../state/licences/reducer';
import { TachoModal } from '../../components/TachoModal';
import { TachoEditModalTitle } from '../../components/TachoModal/TachoEditTitle';
import { mapModulesIdsToItems } from '../../pages/modules/services/moduleServices';
import { PaymentStatusEnum } from '../../../core/domainModels/licences/PaymentStatus';
import { getKeyFromEnum } from '../../../core/helpers/enumUtils';
import { getDistributorIdForLicenseType, isDeveloperLicenseType, maxInt, modulesChange } from './services/licenceService';
import { checkWrongDate, createDateFromTimeZone, dateToStringFormat } from '../../../core/helpers/moment';
import { LicenseTypeEnum } from '../../../core/domainModels/licences/LicenseType';
import useLicenseTypesOptions from '../../hooks/useLicenseTypesOptions';
interface GenerateLicenceModalProps {
    isOpen: boolean;
    onToggle: () => void;
}
export type LicenceErrors = {
    validFrom?: boolean;
    validTo?: boolean;
    paymentDeadline?: boolean;
    maximumNumberOfSupportedCars?: boolean;
    clientId?: boolean;
    allowedModules?: boolean;
    distributorId?: boolean;
};
export const GenerateLicenceModal = (props: GenerateLicenceModalProps) => {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch<DispatchType>();
    const modules = useSelector((state: ApplicationState) => state.modules.modules);
    const generateLicencePending = useSelector((state: ApplicationState) => state.licences.generateLicencePending);
    const generateLicenceError = useSelector((state: ApplicationState) => state.licences.generateLicenceError);
    const [licenceData, setLicenceData] = useState<GenerateLicenseModel>(defaultGenerateLicenseModel);
    const clients = useSelector((state: ApplicationState) => state.clients.clients);
    const [errors, setErrors] = useState<LicenceErrors>();
    const distributors = useSelector((state: ApplicationState) => state.distributors.distributors);

    const validateForm = () => {
        const validated = {
            validFrom: !licenceData.validFrom.isValid,
            validTo: !licenceData.validTo.isValid,
            paymentDeadline: !licenceData.paymentDeadline.isValid,
            maximumNumberOfSupportedCars: licenceData.maximumNumberOfSupportedCars === 0,
            clientId: !licenceData.clientId,
            allowedModules:
                licenceData.allowedModules.length === 0 || licenceData.allowedModules.some(item => item.maximumLoggedUsers === 0),
            distributorId: !isDeveloperLicenseType(licenceData.type) && !licenceData.distributorId,
        };
        setErrors(validated);
        return (
            !validated?.allowedModules &&
            !validated?.clientId &&
            !validated?.maximumNumberOfSupportedCars &&
            !validated?.paymentDeadline &&
            !validated?.validFrom &&
            !validated?.validTo &&
            !validated?.distributorId
        );
    };
    const onSubmit = () => {
        if (validateForm()) {
            dispatch(
                generateLicenceThunk({
                    ...licenceData,
                    frontId: generateGuid(),
                    maximumNumberOfSupportedCars: licenceData.noCarsLimit ? maxInt : licenceData.maximumNumberOfSupportedCars,
                    distributorId: getDistributorIdForLicenseType(licenceData.type, licenceData.distributorId),
                }),
            )
                .unwrap()
                .then(result => {
                    if (result) {
                        setLicenceData(defaultGenerateLicenseModel);
                        props.onToggle();
                        dispatch(fetchLicenceThunk());
                        setErrors({});
                    }
                })
                .catch((err: Error) => console.log(err));
        }
    };
    const clientOptions = useMemo(() => clients.map(item => ({ id: item.id, label: item.name })), [clients]);
    const paymentStatusOptions = useMemo(
        (): Array<Item> => [
            { id: PaymentStatusEnum.Awaiting, label: formatMessage({ id: PaymentStatusEnum.Awaiting }) },
            { id: PaymentStatusEnum.Received, label: formatMessage({ id: PaymentStatusEnum.Received }) },
        ],
        [],
    );

    const licenseTypesOptions = useLicenseTypesOptions();

    const onChangeDateFrom = (value: string) => {
        const change = { ...licenceData };
        if (
            !licenceData.validTo ||
            checkWrongDate(licenceData.validTo) ||
            licenceData.validTo < createDateFromTimeZone(value as string).startOf('day')
        )
            change.validTo = createDateFromTimeZone(value as string)
                .endOf('day')
                .plus({ years: 1 });
        setErrors({ ...errors, validFrom: undefined, validTo: undefined });
        return setLicenceData({
            ...change,
            validFrom: createDateFromTimeZone(value as string).startOf('day'),
        });
    };
    const onChangeDateTo = (value: string) => {
        const change = { ...licenceData };
        if (
            !licenceData.validFrom ||
            checkWrongDate(licenceData.validFrom) ||
            licenceData.validFrom > createDateFromTimeZone(value as string).endOf('day')
        )
            change.validFrom = createDateFromTimeZone(value).startOf('month').minus({ years: 1 });
        setErrors({ ...errors, validFrom: undefined, validTo: undefined });
        return setLicenceData({
            ...change,
            validTo: createDateFromTimeZone(value as string).endOf('day'),
        });
    };

    const distributorsOptions = useMemo((): Array<Item> => {
        return distributors.map(x => ({
            id: x.id,
            label: x.name,
        }));
    }, [distributors]);

    return (
        <TachoModal
            size={'md'}
            height={'auto'}
            isOpen={props.isOpen}
            onToggle={() => {
                setLicenceData(defaultGenerateLicenseModel);
                props.onToggle();
                setErrors({});
            }}
            onSubmit={onSubmit}
            title={<TachoEditModalTitle titleToTranslation="licencesPageGenerateLicence" icon={Icon.Add} />}
            submitLabelToTranslate="licencesPageGenerate"
            submitIcon={Icon.Add}
            isPending={generateLicencePending}
            errorToTranslate={generateLicenceError}
        >
            <Container fluid className="d-flex flex-column px-4 gap-2">
                <Row>
                    <SelectInput
                        showSearchBar
                        label={formatMessage({ id: 'licencesClient' })}
                        value={clientOptions.find(item => item.id === licenceData.clientId)?.label || ''}
                        options={clientOptions.sort((a, b) => a.label.localeCompare(b.label))}
                        onChange={value => {
                            setLicenceData({ ...licenceData, clientId: (value as Item).id });
                            setErrors({ ...errors, clientId: undefined });
                        }}
                        errorMessage={errors?.clientId ? formatMessage({ id: 'licenceEmptyClient' }) : undefined}
                    />
                </Row>
                <Row>
                    <ToogleSwitch
                        value={licenceData.isServiceEdition}
                        label={formatMessage({ id: 'licencesIsServiceEdition' })}
                        onChange={value => setLicenceData({ ...licenceData, isServiceEdition: value })}
                    />
                </Row>
                <Row>
                    <ToogleSwitch
                        value={licenceData.noCarsLimit || false}
                        label={formatMessage({ id: 'licencesNoCarsLimit' })}
                        onChange={value =>
                            setLicenceData({
                                ...licenceData,
                                noCarsLimit: value,
                            })
                        }
                    />
                </Row>
                <Row>
                    <ToogleSwitch
                        value={licenceData.includeBeta || false}
                        label={formatMessage({ id: 'licencesIncludeBeta' })}
                        onChange={value =>
                            setLicenceData({
                                ...licenceData,
                                includeBeta: value,
                            })
                        }
                    />
                </Row>
                <Row>
                    <NumericInput
                        labelLength={5}
                        inputLength={1}
                        value={licenceData.maximumNumberOfSupportedCars}
                        disabled={licenceData.noCarsLimit}
                        min={1}
                        max={100}
                        label={formatMessage({ id: 'licencesMaximumNumberOfSupportedCars' })}
                        onChange={value => setLicenceData({ ...licenceData, maximumNumberOfSupportedCars: value })}
                    />
                </Row>
                <Row>
                    <MultiSelectList
                        hideSelectAll
                        uncontrolled
                        labelLength={-1}
                        rowsToShow={3}
                        label={formatMessage({ id: 'licencesAllowedModules' })}
                        value={mapModulesIdsToItems(licenceData.allowedModules, modules)}
                        placeholder={formatMessage({ id: 'commonSearch' })}
                        selectedLabelsText={formatMessage({ id: 'commonSelected' })}
                        onChange={selectedItems => {
                            setLicenceData(prevState => ({
                                ...licenceData,
                                allowedModules:
                                    modulesChange(selectedItems, prevState)
                                        ?.filter(item => item.checked)
                                        .map(item => ({
                                            tachoModuleId: item.id,
                                            maximumLoggedUsers: (item.additionalValue as number) || 1,
                                        })) || [],
                            }));

                            setErrors({
                                ...errors,
                                allowedModules: selectedItems.filter(item => item.checked).length > 0 ? undefined : true,
                            });
                        }}
                        additionalInput={InputType.Numeric}
                        onAdditionalInputChange={selectedItems => {
                            setLicenceData({
                                ...licenceData,
                                allowedModules: selectedItems
                                    .filter(item => item.checked)
                                    .map(item => ({
                                        tachoModuleId: item.id,
                                        maximumLoggedUsers: (item.additionalValue as number) || 1,
                                    })),
                            });
                        }}
                        errorMessage={errors?.allowedModules ? formatMessage({ id: 'licenceAllowedModulesError' }) : undefined}
                    />
                </Row>

                <Row>
                    <Col>
                        <DateInput
                            locale={config.language}
                            value={dateToStringFormat(licenceData.validFrom)}
                            label={formatMessage({ id: 'licencesValidFrom' })}
                            onChange={onChangeDateFrom}
                            errorMessage={errors?.validFrom ? formatMessage({ id: 'licenceEmptyOrWrongDate' }) : undefined}
                        />
                    </Col>
                    <Col>
                        <DateInput
                            locale={config.language}
                            value={dateToStringFormat(licenceData.validTo)}
                            label={formatMessage({ id: 'licencesValidTo' })}
                            onChange={onChangeDateTo}
                            errorMessage={errors?.validTo ? formatMessage({ id: 'licenceEmptyOrWrongDate' }) : undefined}
                        />
                    </Col>
                </Row>
                <Row>
                    <DateInput
                        locale={config.language}
                        value={dateToStringFormat(licenceData.paymentDeadline)}
                        label={formatMessage({ id: 'licencesPaymentDeadline' })}
                        onChange={value => {
                            setLicenceData({ ...licenceData, paymentDeadline: createDateFromTimeZone(value) });
                            setErrors({ ...errors, paymentDeadline: undefined });
                        }}
                        errorMessage={errors?.paymentDeadline ? formatMessage({ id: 'licenceEmptyOrWrongDate' }) : undefined}
                    />
                </Row>

                <Row>
                    <SelectInput
                        label={formatMessage({ id: 'licencesPaymentStatus' })}
                        value={
                            paymentStatusOptions.find(item => item.id === PaymentStatusEnum[licenceData.paymentStatus])?.label ||
                            ''
                        }
                        options={paymentStatusOptions}
                        onChange={value =>
                            setLicenceData({
                                ...licenceData,
                                paymentStatus: getKeyFromEnum(Object.entries(PaymentStatusEnum), (value as Item).id),
                            })
                        }
                    />
                </Row>
                <Row>
                    <SelectInput
                        label={formatMessage({ id: 'licencesLicenseType' })}
                        value={licenseTypesOptions.find(item => item.id === LicenseTypeEnum[licenceData.type])?.label || ''}
                        options={licenseTypesOptions}
                        onChange={value => {
                            const newType = getKeyFromEnum(Object.entries(LicenseTypeEnum), (value as Item).id);
                            setLicenceData({
                                ...licenceData,
                                type: newType,
                                distributorId: getDistributorIdForLicenseType(newType, licenceData.distributorId),
                            });
                        }}
                    />
                </Row>
                {!isDeveloperLicenseType(licenceData.type) && (
                    <Row>
                        <SelectInput
                            label={formatMessage({ id: 'licencesDistributor' })}
                            value={distributors.find(item => item.id === licenceData.distributorId)?.name || ''}
                            options={distributorsOptions}
                            onChange={value => {
                                setLicenceData({
                                    ...licenceData,
                                    distributorId: (value as Item).id,
                                });
                                setErrors({ ...errors, distributorId: undefined });
                            }}
                            errorMessage={errors?.distributorId ? formatMessage({ id: 'licenceEmptyDistributor' }) : undefined}
                        />
                    </Row>
                )}
                <Row>
                    <ToogleSwitch
                        value={licenceData.sendLicenseToTheClientEmail}
                        label={formatMessage({ id: 'licencesSendLicenceToTheClientEmail' })}
                        onChange={value => setLicenceData({ ...licenceData, sendLicenseToTheClientEmail: value })}
                    />
                </Row>
                <Row>
                    <TextArea
                        align={Alignment.flexStart}
                        value={licenceData.notes}
                        label={formatMessage({ id: 'clientNotes' })}
                        onChange={value => setLicenceData({ ...licenceData, notes: value as string })}
                        labelLength={-1}
                        gap={10}
                    />
                </Row>
            </Container>
        </TachoModal>
    );
};
